/**
 * WeaponButtonsBar.ts
 *
 * Barra de botones de armas en la parte inferior de la pantalla.
 * Usa Rex UI para crear botones interactivos mapeados a X, Z, C.
 */

import { Scene, GameObjects } from 'phaser';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { SlotType, SLOT_KEYS } from '../weapons/DataManagerWeapon';
import { LayerDepths } from '../constants/enums';
import { t } from '../i18n/Localization';

// Colors
const COLORS = {
  BUTTON_BG: 0x2d2d44,
  BUTTON_BG_HOVER: 0x3d3d54,
  BUTTON_BG_PRESSED: 0x1d1d34,
  BUTTON_BORDER: 0x4a9c6d,
  BUTTON_BORDER_INACTIVE: 0x555555,
  GUN_COLOR: 0xff6b6b,      // Red for gun
  TRACTOR_COLOR: 0x4ecdc4,  // Cyan for tractor
  BOMB_COLOR: 0xffe66d,     // Yellow for bomb
  TEXT_PRIMARY: '#ffffff',
  TEXT_SECONDARY: '#aaaaaa',
};

export interface WeaponButtonsBarConfig {
  scene: Scene;
  y?: number;  // Position from bottom (default: 50)
}

interface ButtonInfo {
  slot: SlotType;
  label: string;
  key: string;
  color: number;
}

/**
 * WeaponButtonsBar - Horizontal bar with 3 weapon buttons
 */
export class WeaponButtonsBar {
  private scene: Scene;
  private rexUI: UIPlugin;
  private container: GameObjects.Container;
  private buttons: Map<SlotType, any> = new Map();
  private buttonWidth: number = 210;   // Width of each button (action bar style)
  private buttonHeight: number = 90;   // Height for horizontal layout
  private spacing: number = 50;        // Space between buttons
  private yPosition: number;

  // Button definitions (initialized in constructor for i18n)
  private buttonInfos: ButtonInfo[] = [];

  constructor(config: WeaponButtonsBarConfig) {
    this.scene = config.scene;
    this.rexUI = (config.scene as any).rexUI as UIPlugin;
    this.yPosition = config.y ?? 50;  // Reduced for smaller button height

    this.buttonInfos = [
      { slot: 'tractor', label: t('slot.tractor'), key: 'Z', color: COLORS.TRACTOR_COLOR },
      { slot: 'gun', label: t('slot.gun'), key: 'X', color: COLORS.GUN_COLOR },
      { slot: 'bomb', label: t('slot.bomb'), key: 'C', color: COLORS.BOMB_COLOR },
    ];

    this.createButtons();
    this.setupKeyboardListeners();
  }

  /**
   * Create the button bar
   */
  private createButtons(): void {
    const { width, height } = this.scene.scale;
    const centerX = width / 2;
    const bottomY = height - this.yPosition;

    // Container for all buttons
    this.container = this.scene.add.container(centerX, bottomY);
    this.container.setDepth(LayerDepths.HUD);
    this.container.setScrollFactor(0);

    // Calculate total width
    const totalWidth = (this.buttonWidth * 3) + (this.spacing * 2);
    const startX = -totalWidth / 2 + this.buttonWidth / 2;

    // Create each button
    this.buttonInfos.forEach((info, index) => {
      const x = startX + index * (this.buttonWidth + this.spacing);
      const button = this.createButton(x, 0, info);
      this.buttons.set(info.slot, button);
      this.container.add(button);
    });
  }

  /**
   * Create a single weapon button with horizontal Action Bar layout:
   * [Key] | [WeaponName / SlotLabel] | [Ammo]
   */
  private createButton(x: number, y: number, info: ButtonInfo): any {
    // Create background with solid color
    const background = this.rexUI.add.roundRectangle(0, 0, 0, 0, 10, info.color)
      .setStrokeStyle(3, 0xffffff);

    // Left column: Key letter (large, bold) - dark for contrast
    const keyText = this.scene.add.text(0, 0, info.key, {
      fontFamily: 'Arial Black, Arial',
      fontSize: '42px',
      color: '#1a1a2e',
      fontStyle: 'bold',
    });

    // Center column - Weapon name (top, bold, dark)
    const weaponName = this.scene.add.text(0, 0, t('ui.none'), {
      fontFamily: 'Arial',
      fontSize: '24px',
      color: '#1a1a2e',
      fontStyle: 'bold',
    });

    // Center column - Slot label (bottom, darker)
    const slotLabel = this.scene.add.text(0, 0, info.label, {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#333344',
    });

    // Right column: Ammo text - dark for contrast
    const ammoText = this.scene.add.text(0, 0, '∞', {
      fontFamily: 'Arial',
      fontSize: '22px',
      color: '#1a1a2e',
      fontStyle: 'bold',
    });

    // Center column: vertical sizer with weapon name + slot label
    const centerColumn = this.rexUI.add.sizer({
      orientation: 'y',
      space: { item: 2 },
    })
      .add(weaponName, { align: 'left' })
      .add(slotLabel, { align: 'left' });

    // Main horizontal sizer: [Key] [Center] [Ammo]
    const btn = this.rexUI.add.sizer({
      x: x,
      y: y,
      width: this.buttonWidth,
      height: this.buttonHeight,
      orientation: 'x',
      space: { left: 12, right: 12, top: 0, bottom: 0, item: 8 },
    })
      .addBackground(background)
      .add(keyText, { proportion: 0, align: 'center' })
      .add(centerColumn, { proportion: 1, align: 'center' })
      .add(ammoText, { proportion: 0, align: 'center' });

    // Store references for later updates
    (btn as any).slotType = info.slot;
    (btn as any)._weaponNameText = weaponName;
    (btn as any)._ammoText = ammoText;
    (btn as any)._background = background;
    (btn as any)._baseColor = info.color;

    // Make interactive
    btn.setInteractive({ useHandCursor: true });

    // Hover effects - lighten color
    const hoverColor = this.lightenColor(info.color, 0.2);
    btn.on('pointerover', () => {
      background.setFillStyle(hoverColor);
    });

    btn.on('pointerout', () => {
      background.setFillStyle(info.color);
    });

    // Click - emit weapon action
    btn.on('pointerdown', () => {
      this.onButtonPressed(info.slot);
    });

    btn.on('pointerup', () => {
      this.onButtonReleased(info.slot);
    });

    // Force exact size and layout
    btn.setMinSize(this.buttonWidth, this.buttonHeight);
    btn.layout();
    return btn;
  }

  /**
   * Setup keyboard listeners with console.log
   */
  private setupKeyboardListeners(): void {
    const keyboard = this.scene.input.keyboard;
    if (!keyboard) return;

    // X key - Gun
    keyboard.on('keydown-X', () => {
      this.highlightButton('gun', true);
      this.scene.events.emit('weaponButtonPressed', { slot: 'gun', key: 'X' });
    });

    keyboard.on('keyup-X', () => {
      this.highlightButton('gun', false);
      this.scene.events.emit('weaponButtonReleased', { slot: 'gun', key: 'X' });
    });

    // Z key - Tractor
    keyboard.on('keydown-Z', () => {
      this.highlightButton('tractor', true);
      this.scene.events.emit('weaponButtonPressed', { slot: 'tractor', key: 'Z' });
    });

    keyboard.on('keyup-Z', () => {
      this.highlightButton('tractor', false);
      this.scene.events.emit('weaponButtonReleased', { slot: 'tractor', key: 'Z' });
    });

    // C key - Bomb
    keyboard.on('keydown-C', () => {
      this.highlightButton('bomb', true);
      this.scene.events.emit('weaponButtonPressed', { slot: 'bomb', key: 'C' });
    });

    keyboard.on('keyup-C', () => {
      this.highlightButton('bomb', false);
      this.scene.events.emit('weaponButtonReleased', { slot: 'bomb', key: 'C' });
    });
  }

  /**
   * Handle button pressed (from UI click)
   */
  private onButtonPressed(slot: SlotType): void {
    const key = SLOT_KEYS[slot];
    this.highlightButton(slot, true);
    this.scene.events.emit('weaponButtonPressed', { slot, key });
  }

  /**
   * Handle button released (from UI click)
   */
  private onButtonReleased(slot: SlotType): void {
    const key = SLOT_KEYS[slot];
    this.highlightButton(slot, false);
    this.scene.events.emit('weaponButtonReleased', { slot, key });
  }

  /**
   * Highlight button when pressed
   */
  private highlightButton(slot: SlotType, pressed: boolean): void {
    const btn = this.buttons.get(slot);
    if (!btn) return;

    const bg = (btn as any)._background;
    const baseColor = (btn as any)._baseColor;
    if (bg && baseColor !== undefined) {
      const color = pressed ? this.darkenColor(baseColor, 0.3) : baseColor;
      bg.setFillStyle(color);
    }

    // Scale effect
    if (pressed) {
      this.scene.tweens.add({
        targets: btn,
        scaleX: 0.95,
        scaleY: 0.95,
        duration: 50,
        ease: 'Power2',
      });
    } else {
      this.scene.tweens.add({
        targets: btn,
        scaleX: 1,
        scaleY: 1,
        duration: 100,
        ease: 'Back.easeOut',
      });
    }
  }

  /**
   * Lighten a color by a factor (0-1)
   */
  private lightenColor(color: number, factor: number): number {
    const r = Math.min(255, ((color >> 16) & 0xff) + Math.floor(255 * factor));
    const g = Math.min(255, ((color >> 8) & 0xff) + Math.floor(255 * factor));
    const b = Math.min(255, (color & 0xff) + Math.floor(255 * factor));
    return (r << 16) | (g << 8) | b;
  }

  /**
   * Darken a color by a factor (0-1)
   */
  private darkenColor(color: number, factor: number): number {
    const r = Math.floor(((color >> 16) & 0xff) * (1 - factor));
    const g = Math.floor(((color >> 8) & 0xff) * (1 - factor));
    const b = Math.floor((color & 0xff) * (1 - factor));
    return (r << 16) | (g << 8) | b;
  }

  /**
   * Update ammo display on a button
   */
  updateAmmo(slot: SlotType, ammo: number): void {
    const btn = this.buttons.get(slot);
    if (!btn) return;

    const ammoText = (btn as any)._ammoText as GameObjects.Text;
    if (!ammoText) return;

    // Update text and color based on ammo (dark colors for solid backgrounds)
    if (ammo === -1) {
      ammoText.setText('∞');
      ammoText.setColor('#1a1a2e');  // Dark for infinite
    } else {
      ammoText.setText(ammo.toString());
      // Color based on amount: dark > 10, orange <= 10, dark red <= 3
      if (ammo > 10) {
        ammoText.setColor('#1a1a2e');  // Dark
      } else if (ammo > 3) {
        ammoText.setColor('#663300');  // Dark orange warning
      } else {
        ammoText.setColor('#990000');  // Dark red critical
      }
    }
  }

  /**
   * Update weapon name display on a button
   */
  updateWeaponName(slot: SlotType, name: string): void {
    const btn = this.buttons.get(slot);
    if (!btn) return;

    const nameText = (btn as any)._weaponNameText as GameObjects.Text;
    if (!nameText) return;

    // Truncate long names for horizontal layout
    const maxLength = 12;
    const displayName = name.length > maxLength
      ? name.substring(0, maxLength - 1) + '…'
      : name;

    nameText.setText(displayName || t('ui.none'));

    // Flash animation for feedback when weapon changes
    this.scene.tweens.killTweensOf(nameText);
    nameText.setAlpha(1);
    this.scene.tweens.add({
      targets: nameText,
      alpha: 0.5,
      duration: 100,
      yoyo: true,
      repeat: 1,
      ease: 'Power2',
    });

    // Re-layout button to accommodate new text
    btn.layout();
  }

  /**
   * Show/hide the bar
   */
  setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  /**
   * Get visibility state
   */
  isVisible(): boolean {
    return this.container.visible;
  }

  /**
   * Get the screen position of a button by slot
   * Returns position in screen coordinates (for HUD notifications)
   */
  getButtonPosition(slot: SlotType): { x: number; y: number } | null {
    const btn = this.buttons.get(slot);
    if (!btn) return null;

    // Button x is local to container, add container position
    return {
      x: this.container.x + btn.x,
      y: this.container.y + btn.y,
    };
  }

  /**
   * Cleanup
   */
  destroy(): void {
    // Remove keyboard listeners
    const keyboard = this.scene.input.keyboard;
    if (keyboard) {
      keyboard.off('keydown-X');
      keyboard.off('keyup-X');
      keyboard.off('keydown-Z');
      keyboard.off('keyup-Z');
      keyboard.off('keydown-C');
      keyboard.off('keyup-C');
    }

    // Destroy buttons
    this.buttons.forEach((btn) => {
      this.scene.tweens.killTweensOf(btn);
      btn.destroy();
    });
    this.buttons.clear();

    // Destroy container
    this.container?.destroy();
  }
}
